import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  MenuItem,
  Input,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { isValid } from "date-fns";
import {
  inchesToCm,
  cmToInches,
  lbsToKg,
  kgToLbs,
} from "../../../utils/unitConversion"; // Adjusted path

const Step2Profile = ({
  formData,
  handleChange,
  handleFileChange,
  nextStep,
  prevStep,
}) => {
  const [error, setError] = useState("");
  const [rawBirthDateInput, setRawBirthDateInput] = useState('');

  // Effect to initialize and sync rawBirthDateInput from formData.birthDate
  useEffect(() => {
    if (formData.birthDate) {
      const date = new Date(formData.birthDate);
      if (isValid(date)) {
        // Format date to YYYY-MM-DD for the input field
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setRawBirthDateInput(`${year}-${month}-${day}`);
      } else {
        // If formData.birthDate is not valid (e.g. an invalid string was somehow set)
        setRawBirthDateInput('');
      }
    } else {
      setRawBirthDateInput('');
    }
  }, [formData.birthDate]);

  const handleBirthDateBlur = () => {
    const trimmedInput = rawBirthDateInput.trim();
    if (trimmedInput === "") {
      handleChange({ target: { name: 'birthDate', value: null } });
      // Clear only birth date error if it exists
      if (typeof error === 'string' && error.includes("Birth Date")) setError("");
      return;
    }

    // Try to parse with a specific format YYYY-MM-DD
    const parts = trimmedInput.split('-');
    let parsedDate = null;
    if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(parts[2], 10);
        // Basic validation for year, month, day ranges
        if (year > 1000 && year < 3000 && month >= 0 && month <= 11 && day >= 1 && day <= 31) {
            const dateAttempt = new Date(Date.UTC(year, month, day)); // Use UTC to avoid timezone shifts affecting day
            // Check if Date constructor created a date that matches these parts
            if (dateAttempt.getUTCFullYear() === year && dateAttempt.getUTCMonth() === month && dateAttempt.getUTCDate() === day) {
                parsedDate = dateAttempt;
            }
        }
    }

    if (parsedDate && isValid(parsedDate)) {
      handleChange({ target: { name: 'birthDate', value: parsedDate } });
      if (typeof error === 'string' && error.includes("Birth Date")) setError("");
    } else {
      handleChange({ target: { name: 'birthDate', value: null } });
      setError("Invalid Birth Date. Please use YYYY-MM-DD or pick from the calendar.");
    }
  };

  // Helper to check if a value is a positive number
  const isPositiveNumber = (value) => {
    const num = parseFloat(value);
    return !isNaN(num) && num > 0;
  };

  const handleNext = () => {
    if (
      !formData.firstname ||
      !formData.lastname ||
      !formData.gender ||
      !formData.birthDate ||
      !isValid(new Date(formData.birthDate))
    ) {
      setError(
        "First Name, Last Name, Gender, and valid Birth Date are required."
      );
      return;
    }

    // Validate height
    if (formData.height && !isPositiveNumber(formData.height)) {
      const unit = formData.unitPreference === "metric" ? "cm" : "inches";
      setError(`Height must be a valid positive number in ${unit}.`);
      return;
    }

    // Validate weight
    if (formData.weight && !isPositiveNumber(formData.weight)) {
      const unit = formData.unitPreference === "metric" ? "kg" : "lbs";
      setError(`Weight must be a valid positive number in ${unit}.`);
      return;
    }

    setError("");
    nextStep();
  };

  const handleUnitSensitiveChange = (e) => {
    const { name, value } = e.target;
    let metricValue = value;

    if (value === "") {
      // Allow clearing the field
      handleChange({ target: { name, value: "" } });
      return;
    }

    if (!/^\d*\.?\d*$/.test(value)) {
      // Basic validation for numeric input
      setError(`Please enter a valid number for ${name}.`);
      // Optionally, don't update formData if invalid, or revert to previous valid value
      // For now, we'll let the user see their input but error will show
      handleChange({ target: { name, value } }); // Still pass to allow seeing input
      return;
    } else {
      setError(""); // Clear error if input becomes valid
    }

    if (formData.unitPreference === "imperial") {
      if (name === "height") {
        metricValue = inchesToCm(value);
      } else if (name === "weight") {
        metricValue = lbsToKg(value);
      }
    }
    // Ensure metricValue is stored as a string if it's a number, or keep as empty string
    handleChange({
      target: {
        name,
        value:
          typeof metricValue === "number" ? String(metricValue) : metricValue,
      },
    });
  };

  return (
    <Box component="form" sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mt: 1 }}>
        Basic Information
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="firstname"
            label="First Name"
            name="firstname"
            autoComplete="given-name"
            value={formData.firstname || ""}
            onChange={handleChange}
            error={!!(error && error.includes("First Name"))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="lastname"
            label="Last Name"
            name="lastname"
            autoComplete="family-name"
            value={formData.lastname || ""}
            onChange={handleChange}
            error={!!(error && error.includes("Last Name"))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            required
            fullWidth
            id="gender"
            label="Gender"
            name="gender"
            value={formData.gender || ""}
            onChange={handleChange}
            error={!!(error && error.includes("Gender"))}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Birth Date"
              value={formData.birthDate || null} // Keep this for calendar and initial valid date
              onChange={(newValue) => {
                // This handles selection from calendar popup
                handleChange({ target: { name: 'birthDate', value: newValue } });
                if (newValue && isValid(new Date(newValue))) {
                  const date = new Date(newValue);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  setRawBirthDateInput(`${year}-${month}-${day}`);
                  if (typeof error === 'string' && error.includes("Birth Date")) setError("");
                } else if (!newValue) {
                   setRawBirthDateInput(""); // Clear input if date is cleared from picker
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  required
                  value={rawBirthDateInput} // Bind to raw input state
                  onChange={(e) => setRawBirthDateInput(e.target.value)} // Update raw input state
                  onBlur={handleBirthDateBlur} // Validate on blur
                  error={!!(typeof error === 'string' && error.includes("Birth Date"))} // Show error if present
                  helperText={ (typeof error === 'string' && error.includes("Birth Date")) ? error : params.helperText }
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 2 }}>
        Physical Attributes
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="text" // Changed to text to handle decimal points correctly during conversion display
            id="height"
            label={`Height (${
              formData.unitPreference === "metric" ? "cm" : "in"
            })`}
            name="height"
            value={
              formData.height === ""
                ? ""
                : formData.unitPreference === "metric"
                ? formData.height
                : cmToInches(formData.height)
            }
            onChange={handleUnitSensitiveChange}
            error={
              !!(
                error &&
                (error.includes("Height") || error.includes("height"))
              )
            }
            helperText={
              error && (error.includes("Height") || error.includes("height"))
                ? error
                : ""
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="text" // Changed to text to handle decimal points correctly during conversion display
            id="weight"
            label={`Weight (${
              formData.unitPreference === "metric" ? "kg" : "lbs"
            })`}
            name="weight"
            value={
              formData.weight === ""
                ? ""
                : formData.unitPreference === "metric"
                ? formData.weight
                : kgToLbs(formData.weight)
            }
            onChange={handleUnitSensitiveChange}
            error={
              !!(
                error &&
                (error.includes("Weight") || error.includes("weight"))
              )
            }
            helperText={
              error && (error.includes("Weight") || error.includes("weight"))
                ? error
                : ""
            }
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl
            fullWidth
            error={!!(error && error.includes("Unit Preference"))}
          >
            <InputLabel id="unitPreference-label">Unit Preference</InputLabel>
            <Select
              labelId="unitPreference-label"
              id="unitPreference"
              name="unitPreference"
              value={formData.unitPreference || "metric"}
              onChange={(e) => {
                // When unit preference changes, clear validation errors related to height/weight
                if (
                  error.includes("Height") ||
                  error.includes("Weight") ||
                  error.includes("height") ||
                  error.includes("weight")
                ) {
                  setError("");
                }
                handleChange(e);
              }}
              label="Unit Preference"
            >
              <MenuItem value="metric">Metric (cm/kg)</MenuItem>
              <MenuItem value="imperial">Imperial (in/lbs)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 2 }}>
        Education & Career
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            id="educationLevel"
            label="Education Level"
            name="educationLevel"
            value={formData.educationLevel || ""}
            onChange={handleChange}
          >
            <MenuItem value="high_school">High School</MenuItem>
            <MenuItem value="associates">Associate's</MenuItem>
            <MenuItem value="bachelors">Bachelor's</MenuItem>
            <MenuItem value="masters">Master's</MenuItem>
            <MenuItem value="doctorate">Doctorate</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="academicMajor"
            label="Academic Major"
            name="academicMajor"
            value={formData.academicMajor || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            id="socioeconomicStatus"
            label="Socioeconomic Status"
            name="socioeconomicStatus"
            value={formData.socioeconomicStatus || ""}
            onChange={handleChange}
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="lower_middle">Lower-Middle</MenuItem>
            <MenuItem value="middle">Middle</MenuItem>
            <MenuItem value="upper_middle">Upper-Middle</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            id="maritalStatus"
            label="Marital Status"
            name="maritalStatus"
            value={formData.maritalStatus || ""}
            onChange={handleChange}
          >
            <MenuItem value="single">Single</MenuItem>
            <MenuItem value="married">Married</MenuItem>
            <MenuItem value="divorced">Divorced</MenuItem>
            <MenuItem value="widowed">Widowed</MenuItem>
            <MenuItem value="separated">Separated</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="career"
            label="Career"
            name="career"
            value={formData.career || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            id="householdSize"
            label="Household Members"
            name="householdSize"
            value={formData.householdSize || ""}
            onChange={handleChange}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
            <MenuItem value="8+">8+</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 2 }}>
        Health & Lifestyle
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            id="gymAccess"
            label="Access to Gym"
            name="gymAccess"
            value={formData.gymAccess || ""}
            onChange={handleChange}
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            id="dietaryRestrictions"
            label="Dietary Restrictions"
            name="dietaryRestrictions"
            value={formData.dietaryRestrictions || ""}
            onChange={handleChange}
          >
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="vegetarian">Vegetarian</MenuItem>
            <MenuItem value="vegan">Vegan</MenuItem>
            <MenuItem value="halal">Halal</MenuItem>
            <MenuItem value="kosher">Kosher</MenuItem>
            <MenuItem value="gluten_free">Gluten-Free</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="disabilities"
            label="Disabilities"
            name="disabilities"
            value={formData.disabilities || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="injuries"
            label="Major Injuries"
            name="injuries"
            value={formData.injuries || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="currentHealthIssues"
            label="Current Health Issues"
            name="currentHealthIssues"
            value={formData.currentHealthIssues || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="medications"
            label="Medications"
            name="medications"
            value={formData.medications || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="allergies"
            label="Allergies"
            name="allergies"
            value={formData.allergies || ""}
            onChange={handleChange}
          />
        </Grid>
      </Grid>
      <Input
        type="file"
        fullWidth
        id="profilePicture"
        name="profilePicture"
        onChange={handleFileChange}
        sx={{ mt: 2 }}
      />
      {/* Display preview of profile picture */}
      {formData.profilePicture &&
        typeof formData.profilePicture !== "string" && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2">Profile Picture Preview:</Typography>
            <img
              src={URL.createObjectURL(formData.profilePicture)}
              alt="Profile Preview"
              style={{
                width: "100px",
                height: "100px",
                objectFit: "cover",
                marginTop: "8px",
              }}
            />
          </Box>
        )}
      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
      <Box
        sx={{ display: "flex", justifyContent: "space-between", mt: 3, mb: 2 }}
      >
        <Button variant="outlined" onClick={prevStep}>
          Previous
        </Button>
        <Button variant="contained" onClick={handleNext}>
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default Step2Profile;
